import { Fragment, useState, type ReactNode } from 'react';
import { formatTimestamp } from '@pc/lib/time';
import { Link } from '@tanstack/react-router';
import type { operations } from '@pc/api/schema';
import { KnownPerson } from '../accounts/KnownPerson';
import { Absent, Badge, Empty, Facts, Id, OpId, type Tone } from '../ui';

/**
 * searchAuditTrail and getAuditRecordsByOpId return the identical record
 * shape, so this one type serves both call sites instead of being restated
 * per screen.
 */
export type AuditRecord =
  operations['searchAuditTrail']['responses'][200]['content']['application/json']['records'][number];

/**
 * The vocabulary the `?targetType` filter will accept, taken from the operation
 * rather than restated, so a thirteenth kind of target reaches every filter in
 * this app through `npm run api:types` and nothing else.
 */
export type AuditTargetType = NonNullable<
  NonNullable<operations['searchAuditTrail']['parameters']['query']>['targetType']
>;

/**
 * The same twelve values as the type above, as data a runtime check can consult
 * — a TypeScript union is erased long before anything can ask whether a string
 * from an API response or a URL is a member of it.
 *
 * Written out rather than read from `openapi.json` at runtime. Importing that
 * document put all 459 KB of it into the main bundle, to recover twelve
 * strings, and made every page load parse it.
 *
 * The reason to derive it was that a hand-transcribed list drifts silently: it
 * keeps compiling while quietly refusing a target kind the backend has started
 * emitting. The exhaustiveness check below removes that risk without the
 * bundle. `satisfies` proves every entry is a real member; `Exclude` proves no
 * member is missing, so `npm run api:types` bringing in a thirteenth value
 * fails the typecheck here until it is added.
 */
export const AUDIT_TARGET_TYPES = [
  'account',
  'account-list',
  'account-resources',
  'actor',
  'audit-log',
  'gateway-bind',
  'job-list',
  'lxc',
  'lxc-capabilities',
  'lxc-gateway-bind',
  'lxc-gateway-binds',
  'lxc-list',
] as const satisfies readonly AuditTargetType[];

/*
 * A compile error naming the missing member, rather than a list that drifts.
 *
 * The tuple wrapper is load-bearing: a bare conditional distributes over a
 * union, and `never` distributes to `never`, so the check would pass whatever
 * was missing. `[T] extends [never]` compares the whole type at once.
 */
type MissingTargetType = Exclude<AuditTargetType, (typeof AUDIT_TARGET_TYPES)[number]>;
const _everyTargetTypeListed: [MissingTargetType] extends [never]
  ? true
  : ['AUDIT_TARGET_TYPES is missing', MissingTargetType] = true;
void _everyTargetTypeListed;

/**
 * Whether a target type the app was handed is one the filter would accept.
 *
 * A stored record's `targetType` is still declared `{ type: "string", nullable:
 * true }` while the filter parameter is the enum above, and that asymmetry is
 * load-bearing rather than an oversight: a row written before a value was
 * retired, or by a backend deployed ahead of this bundle, genuinely can carry a
 * word this search would reject. So can a hand-edited URL.
 */
export function isAuditTargetType(value: string): value is AuditTargetType {
  return (AUDIT_TARGET_TYPES as readonly string[]).includes(value);
}

/**
 * The shape the contract puts on every Discord id it will accept: the accounts
 * screen's `discordID` path parameter and this operation's own
 * `actorDiscordID` and `subjectDiscordID` filters all declare
 * `^[1-9][0-9]{16,19}$`. A stored record carries those ids as plain nullable
 * strings with no such promise, so this is what decides whether one becomes a
 * link — an id of any other shape names nothing `/accounts` can look up, and a
 * link that lands on a refusal is worse than the text it replaced.
 */
const DISCORD_ID_PATTERN = /^[1-9][0-9]{16,19}$/;

const OUTCOME_TONE: Record<AuditRecord['outcome'], Tone> = {
  SUCCESS: 'good',
  FAILURE: 'bad',
  // Denied is not a failure — the system worked and refused the actor. Warn,
  // not bad, so it does not read as the same thing as a broken command.
  DENIED: 'warn',
};

/**
 * Why a `DENIED` or `FAILURE` row is not a `SUCCESS` — the entire point of
 * scanning this trail for a refusal, so it belongs beside `Outcome` rather
 * than a fact an operator only sees after expanding the row.
 *
 * A `SUCCESS` row usually carries no reason at all, and that absence is
 * unremarkable — an empty cell, not `<Absent />`. The same null on a
 * `DENIED` or `FAILURE` row is the one this table's existing absent
 * treatment already exists for: a row that could have explained itself and
 * did not.
 */
function ReasonCell({ record }: { record: AuditRecord }) {
  if (record.reason !== null) return <>{record.reason}</>;
  if (record.outcome === 'SUCCESS') return null;
  return <Absent />;
}

/**
 * `via: 'break-glass'` is the emergency path that bypasses normal checks.
 * Worth a badge of its own so it does not blend into ordinary traffic during
 * a scan of the table.
 */
function ViaCell({ via }: { via: AuditRecord['via'] }) {
  if (via === 'break-glass') return <Badge tone="warn">break-glass</Badge>;
  return <>{via}</>;
}

/**
 * A Discord id as the way into the screen that describes that person.
 *
 * The id is handed to the router as the string it arrived as. Every snowflake
 * is larger than Number.MAX_SAFE_INTEGER, so anything that lets it pass
 * through Number() on its way into a URL points at a *different account* and
 * says nothing about having done so.
 */
function AccountLink({
  discordID,
  title,
  children,
}: {
  discordID: string | null;
  title: string;
  children?: ReactNode;
}) {
  if (discordID === null) return <>{children ?? <Absent />}</>;

  const label = children ?? <Id>{discordID}</Id>;
  if (!DISCORD_ID_PATTERN.test(discordID)) return <>{label}</>;

  return (
    <Link to="/accounts" search={{ discordID }} title={title}>
      {label}
    </Link>
  );
}

function describeActor(record: AuditRecord): ReactNode {
  const { actor, actorUsername, actorDiscordID } = record;
  if (actor?.displayName) return actor.displayName;
  if (actor?.email) return actor.email;
  if (actor?.oktaId) return actor.oktaId;
  if (actorUsername) return actorUsername;
  if (actorDiscordID) return <Id>{actorDiscordID}</Id>;
  return <Absent />;
}

/**
 * The actor, however well the record knows them, pointing at their account.
 *
 * An actor reached through `via: 'api'` or `break-glass` may carry an Okta
 * identity and no Discord id at all, and this console has no screen describing
 * an Okta identity — so that case stays plain text here and gets its
 * destination in the expanded row, where "every record for this Okta id" is a
 * real place to go.
 */
function ActorCell({ record }: { record: AuditRecord }) {
  return (
    <AccountLink discordID={record.actorDiscordID} title="Open this actor's account">
      {describeActor(record)}
    </AccountLink>
  );
}

/**
 * The target type says what kind of thing was acted on but not which screen
 * describes it, and guessing from the id's shape would send an operator to a
 * container lookup for something that was never a container. So neither half is
 * routed to a resource screen.
 *
 * What both halves *are* is declared filters of this same search, so each
 * becomes the question it can answer exactly: the type alone, every record of
 * that kind; the pair, everything that ever happened to this one target. That
 * second one is the "what happened to this container" question, asked without
 * having to know what kind of thing it is.
 *
 * A type outside the filter's vocabulary is shown but not linked, and is left
 * out of the id's link as well — one rejected parameter refuses the whole
 * search, so carrying it along would break the question that still works. See
 * `isAuditTargetType` for how a record comes to hold such a value.
 */
function TargetCell({ record }: { record: AuditRecord }) {
  const { targetType, targetId } = record;
  if (targetType === null && targetId === null) return <Absent />;

  const filterable = targetType !== null && isAuditTargetType(targetType) ? targetType : undefined;

  return (
    <>
      {targetType === null ? (
        <Absent label="untyped" />
      ) : filterable === undefined ? (
        targetType
      ) : (
        <Link to="/audit" search={{ targetType: filterable }} title={`Every record with target type ${filterable}`}>
          {filterable}
        </Link>
      )}
      {': '}
      {targetId === null ? (
        <Absent label="no id" />
      ) : (
        <Link
          to="/audit"
          search={{ ...(filterable !== undefined && { targetType: filterable }), targetId }}
          title="Every record for this target"
        >
          <Id>{targetId}</Id>
        </Link>
      )}
    </>
  );
}

/**
 * The row's own identifiers point outward, at the screens describing the
 * people and things involved. These point back inward: the same identifiers as
 * filters, which is the other half of following an incident — what else this
 * actor touched, what else was done to this account. Both are already declared
 * filters of the search, so neither is a guess about how the trail is indexed.
 */
function RelatedSearches({ record }: { record: AuditRecord }) {
  const { actorDiscordID, subjectDiscordID } = record;

  const searches: Array<{ key: string; link: ReactNode }> = [
    ...(actorDiscordID !== null && DISCORD_ID_PATTERN.test(actorDiscordID)
      ? [
          {
            key: 'actor',
            link: (
              <Link to="/audit" search={{ actorDiscordID }}>
                everything this actor did
              </Link>
            ),
          },
        ]
      : []),
    ...(subjectDiscordID !== null && DISCORD_ID_PATTERN.test(subjectDiscordID)
      ? [
          {
            key: 'subject',
            link: (
              <Link to="/audit" search={{ subjectDiscordID }}>
                everything done to this subject
              </Link>
            ),
          },
        ]
      : []),
  ];

  if (searches.length === 0) return null;

  return (
    <p>
      Search the trail for{' '}
      {searches.map(({ key, link }, index) => (
        <Fragment key={key}>
          {index > 0 ? ' · ' : ''}
          {link}
        </Fragment>
      ))}
    </p>
  );
}

export function RecordsTable({
  records,
  hasFilters = true,
}: {
  records: AuditRecord[];
  /**
   * Whether this result came from a search with at least one filter set, as
   * against the unfiltered trail. An empty *filtered* result reads fine as
   * "no matching audit records" — an empty *unfiltered* one would read as
   * "nothing has ever happened here", which for a running console is not a
   * fact this table can know, only a search it has not been asked to narrow.
   *
   * Defaults to `true` — the filtered wording, unchanged from before this
   * prop existed — for the one caller that has no notion of "filters" of its
   * own: `OpIdLookup` is always a search for one exact operation id, so an
   * empty result there is correctly "no matching records", never "no records
   * yet".
   */
  hasFilters?: boolean;
}) {
  // Expansion state keys off `sequence`, the chain's own monotonic counter —
  // stable across re-renders and unique even across pages of the same query.
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  if (records.length === 0) {
    return <Empty>{hasFilters ? 'No matching audit records.' : 'No audit records yet.'}</Empty>;
  }

  function toggle(sequence: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sequence)) next.delete(sequence);
      else next.add(sequence);
      return next;
    });
  }

  return (
    <div className="scroll-x">
      <table>
        <thead>
          <tr>
            <th style={{ textAlign: 'right' }}>Sequence</th>
            <th>Time</th>
            <th>Actor</th>
            <th>Command</th>
            <th>Outcome</th>
            <th>Reason</th>
            <th>Via</th>
            <th>Target</th>
            <th>Op ID</th>
            <th className="row-actions" />
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <RecordRow
              key={record.sequence}
              record={record}
              expanded={expanded.has(record.sequence)}
              onToggle={() => toggle(record.sequence)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * `detail` is typed `{ [key: string]: unknown } | null` in both operations
 * this table renders — openapi.json's searchAuditTrail and
 * getAuditRecordsByOpId responses agree, and neither declares it a string.
 * `JSON.stringify` is the right rendering for that shape: it does not wrap an
 * object in quotes, only the individual string values inside it.
 *
 * A JSON-valued column can still hold a bare string underneath that schema,
 * though, and `JSON.stringify` on one wraps the whole payload in a pair of
 * literal quotes it does not have on screen. This checks the runtime shape
 * rather than trusting the declared one — the same reasoning
 * `searchableFields` in OwnerPicker.tsx gives for not trusting every field
 * the contract marks required.
 */
function formatDetail(detail: AuditRecord['detail']): ReactNode {
  if (!detail) return null;
  return <pre>{typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2)}</pre>;
}

function RecordRow({
  record,
  expanded,
  onToggle,
}: {
  record: AuditRecord;
  expanded: boolean;
  onToggle: () => void;
}) {
  const actorOktaId = record.actor?.oktaId ?? null;

  return (
    <>
      <tr>
        <td style={{ textAlign: 'right' }}>{record.sequence}</td>
        <td>{formatTimestamp(record.createdAt)}</td>
        <td>
          <ActorCell record={record} />
        </td>
        <td>
          {record.command}
          {record.subcommand ? ` ${record.subcommand}` : ''}
        </td>
        <td>
          <Badge tone={OUTCOME_TONE[record.outcome]}>{record.outcome}</Badge>
        </td>
        <td>
          <ReasonCell record={record} />
        </td>
        <td>
          <ViaCell via={record.via} />
        </td>
        <td>
          <TargetCell record={record} />
        </td>
        <td>
          {/* The whole operation, not this one line of it: an op id groups the
              submission with its completion. Rendered through the shared
              control so it is followed and carried the same way here as
              everywhere else it appears. */}
          <OpId opId={record.opId} />
        </td>
        {/* `row-actions` pins this column to the right edge of the scroll
            container — see app.css. The table is wide enough to overflow on
            any realistic viewport, and this control was the one thing in it
            that could only be reached by scrolling there first. */}
        <td className="row-actions">
          <button type="button" onClick={onToggle}>
            {expanded ? 'Hide' : 'Details'}
          </button>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={10}>
            <Facts
              items={[
                ['Sequence', record.sequence],
                ['Reason', record.reason],
                ['Duration', record.durationMs !== null ? `${record.durationMs} ms` : null],
                ['Guild ID', record.guildID],
                [
                  // No longer always an id: a subject already sitting in this
                  // client's cached accounts (see `KnownPerson.tsx`) renders
                  // as the person it names instead.
                  'Subject',
                  <KnownPerson
                    discordID={record.subjectDiscordID}
                    linkTitle="Open the account this action was about"
                  />,
                ],
                [
                  'Actor Okta ID',
                  // No screen in this console describes an Okta identity, so
                  // the only destination that genuinely exists for one is the
                  // trail filtered to it.
                  actorOktaId !== null ? (
                    <Link
                      to="/audit"
                      search={{ actorOktaId }}
                      title="Every record for this Okta identity"
                    >
                      <Id>{actorOktaId}</Id>
                    </Link>
                  ) : null,
                ],
                ['Actor title', record.actor?.title],
                // The actor's level *at the time of the action* — it can
                // change or be revoked afterward, so this is not the same
                // fact as looking the person up today.
                [
                  'Actor level (at the time)',
                  record.actor?.level != null
                    ? `${record.actor.level} (${record.actor.levelSource ?? 'unknown source'})`
                    : null,
                ],
                [
                  'Actor groups',
                  record.actor && record.actor.groups.length > 0 ? record.actor.groups.join(', ') : null,
                ],
                ['Detail', formatDetail(record.detail)],
                ['Previous hash', <code>{record.previousHash}</code>],
                ['Hash', <code>{record.hash}</code>],
              ]}
            />
            <RelatedSearches record={record} />
          </td>
        </tr>
      )}
    </>
  );
}
